import { useCallback, useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';
import { getTimeDiffJson } from '../utils/get-time-from-timestamp';
import { SocketStatus } from '../typings';
import { debounce } from '@mui/material';
import { FallbackSyncController } from '../typings/socket-connection';
import { trackSocketEventActionType, SocketEventActionType } from '../utils/socket-event-logger';

export interface UseSocketOptions {
  /** When true, cleanup will not call socket.disconnect() so the socket can be reused by other consumers. */
  doNotDisconnectOnUnmount?: boolean;
  /** Control whether we try to reconnect on window focus. Defaults to true for backwards compatibility. */
  reconnectOnFocus?: boolean;
  /** Control whether we try to reconnect when the tab becomes visible. Defaults to true for backwards compatibility. */
  reconnectOnVisibility?: boolean;
  /** Number of hours after which we consider the socket too old and force a full reload. Defaults to 6. */
  maxIdleHoursBeforeForcedReconnect?: number;
}

const useSocket = (
  getSocket: () => Socket,
  onReconnect?: (lastUpdatedTimestamp: number) => void,
  checkSocketDisconnect?: boolean,
  onConnectionStatusChange?: (status: boolean) => void,
  fallbackSync?: FallbackSyncController,
  options?: UseSocketOptions
) => {
  const [socket, setSocket] = useState(getSocket());
  const socketName = ((socket as any).nsp! ?? '').substring(1);
  const savedCurrentTimeRef = useRef<number>(new Date().getTime());
  const lastPongReceivedRef = useRef(true);

  const BASE_PONG_TIMEOUT = 3000;
  const RETRY_DELAYS = [2000, 4000, 8000, 12000]; // in ms

  const isConnectedRef = useRef(false);
  const lastUpdatedTimestampRef = useRef(new Date().getTime());
  const onReconnectRef = useRef(onReconnect);
  const BUFFER_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds prev msgs will be fetched
  // we will update on mouseMove and on Socket msg received
  const lastSocketMsgTimeRef = useRef(new Date().getTime());
  const fallbackEnabled = fallbackSync?.enabled;
  const missedPongAttemptsRef = useRef(0);

  onReconnectRef.current = onReconnect;

  const addListener = useCallback(
    (eventName: string, listener: () => void) => {
      socket.on(eventName, listener);
    },
    [socket]
  );

  const removeListener = useCallback(
    (eventName: string, listener: () => void) => {
      socket.off(eventName, listener);
    },
    [socket]
  );

  useEffect(() => {
    // setSocket(getSocket());
    const newSocket = getSocket();
    if (newSocket !== socket) {
      setSocket(newSocket);
    }
  }, [getSocket, setSocket]);

  useEffect(() => {
    if (!checkSocketDisconnect) return;

    let pongTimeout: NodeJS.Timeout | null = null;
    let retryTimeout: NodeJS.Timeout | null = null;

    const markConnectionLost = () => {
      trackSocketEventActionType({
        type: SocketEventActionType.SOCKET_CONNECTION_LOST,
        socketName,
        retryCount: missedPongAttemptsRef.current,
      });
      onConnectionStatusChange?.(false);
      if (fallbackEnabled) fallbackSync?.start();
    };

    const scheduleRetry = () => {
      if (missedPongAttemptsRef.current >= RETRY_DELAYS.length) {
        markConnectionLost();
        return;
      }

      const delay = RETRY_DELAYS[missedPongAttemptsRef.current];
      trackSocketEventActionType({
        type: SocketEventActionType.SOCKET_RETRY_SCHEDULED,
        socketName,
        retryCount: missedPongAttemptsRef.current,
        delayMs: delay,
      });
      retryTimeout = setTimeout(() => {
        // If a pong came in while waiting, don't retry.
        if (lastPongReceivedRef.current) {
          missedPongAttemptsRef.current = 0;
          return;
        }

        missedPongAttemptsRef.current += 1;
        sendPing();
      }, delay);
    };

    const sendPing = () => {
      // If we're already in the middle of retries and still haven't seen a pong,
      // just let the retry chain handle it.
      if (!lastPongReceivedRef.current && missedPongAttemptsRef.current === 0) {
        return;
      }

      lastPongReceivedRef.current = false; // Reset before sending a new ping
      trackSocketEventActionType({
        type: SocketEventActionType.SOCKET_PING_SENT,
        socketName,
        retryCount: missedPongAttemptsRef.current + 1,
      });
      socket.emit('ping'); // Send ping to the server
      // Set a timeout to check for pong for this single attempt.
      if (pongTimeout) clearTimeout(pongTimeout);
      pongTimeout = setTimeout(() => {
        if (!lastPongReceivedRef.current) {
          // We missed this pong, start / continue retries.
          scheduleRetry();
        }
      }, BASE_PONG_TIMEOUT);
    };

    // Any message from the server proves the socket is alive.
    // onAny catches all events including 'pong', so we use it as the single handler
    // to avoid double-firing when both socket.on('pong') and onAny are registered.
    const onServerActivity = (eventName: string) => {
      const isPong = eventName === 'pong';
      const retryCountAtRecovery = missedPongAttemptsRef.current;
      const wasInRetryState = retryCountAtRecovery > 0;

      if (isPong)
        trackSocketEventActionType({
          type: SocketEventActionType.SOCKET_PONG_RECEIVED,
          socketName,
          retryCount: retryCountAtRecovery,
        });

      if (pongTimeout) clearTimeout(pongTimeout);
      if (retryTimeout) clearTimeout(retryTimeout);
      lastPongReceivedRef.current = true;
      missedPongAttemptsRef.current = 0;
      onConnectionStatusChange?.(true);
      fallbackSync?.stop();

      // Only log recovery when we were actually in a degraded state.
      if (wasInRetryState) {
        trackSocketEventActionType({
          type: SocketEventActionType.SOCKET_CONNECTION_RECOVERED,
          socketName,
          via: isPong ? 'pong' : 'any_message',
          retryCount: retryCountAtRecovery,
        });
      }
    };
    socket.on('pong', onServerActivity);
    socket.onAny(onServerActivity);

    const interval = setInterval(sendPing, 5000);

    return () => {
      lastPongReceivedRef.current = true;
      clearInterval(interval);
      if (pongTimeout) clearTimeout(pongTimeout);
      if (retryTimeout) clearTimeout(retryTimeout);
      missedPongAttemptsRef.current = 0;
      fallbackSync?.stop();
      socket.off('pong', onServerActivity);
      socket.offAny(onServerActivity);
    };
  }, [socket, checkSocketDisconnect, onConnectionStatusChange, fallbackEnabled, fallbackSync]);

  useEffect(() => {
    const updateLastMouseMoveTime = () => {
      lastSocketMsgTimeRef.current = new Date().getTime();
    };
    socket.on('message', updateLastMouseMoveTime);
    return () => {
      socket.off('message', updateLastMouseMoveTime);
    };
  }, []);

  useEffect(() => {
    const onConnect = () => {
      updateSocketStatus();
      isConnectedRef.current = true;
      // Reset retry state on successful connect so future disconnects
      // always run the full retry sequence before surfacing "lost".
      missedPongAttemptsRef.current = 0;
      lastPongReceivedRef.current = true;
      onConnectionStatusChange?.(true);
      fallbackSync?.stop();
      trackSocketEventActionType({ type: SocketEventActionType.SOCKET_RECONNECTED, socketName });
    };

    const onDisconnect = (reason: Socket.DisconnectReason) => {
      if (window.socketStatus) {
        window.socketStatus[socketName] = {
          status: SocketStatus.NOT_CONNECTED,
          message: reason || 'Unknown reason',
        };
      } else {
        window.socketStatus = {
          [socketName]: {
            status: SocketStatus.NOT_CONNECTED,
            message: reason || 'Unknown reason',
          },
        };
      }
      if (missedPongAttemptsRef.current >= RETRY_DELAYS.length) {
        onConnectionStatusChange?.(false);
      }

      trackSocketEventActionType({
        type: SocketEventActionType.SOCKET_DISCONNECTED,
        socketName,
        reason: reason ?? 'unknown',
      });
      if (navigator.onLine) {
        socket.connect();
        return;
      }
      isConnectedRef.current = false;
      lastUpdatedTimestampRef.current = new Date().getTime();
      // socket.disconnect();
    };

    socket.connect();
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    return () => {
      if (!options?.doNotDisconnectOnUnmount) {
        socket.disconnect();
      }
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [socket]);

  const updateSocketStatus = () => {
    if (socket.connected) {
      if (window.socketStatus) {
        window.socketStatus[socketName] = {
          status: SocketStatus.CONNECTED,
          message: 'Connected successfully',
        };
      } else {
        window.socketStatus = {
          [socketName]: {
            status: SocketStatus.CONNECTED,
            message: 'Connected successfully',
          },
        };
      }
    }
  };
  useEffect(() => {
    updateSocketStatus();
  }, [socket.connected]);

  useEffect(() => {
    const reconnectOnFocus = options?.reconnectOnFocus ?? true;
    const reconnectOnVisibility = options?.reconnectOnVisibility ?? true;
    const maxIdleHoursBeforeForcedReconnect = options?.maxIdleHoursBeforeForcedReconnect ?? 6;

    const refreshChat = (event?: Event, lastSyncTime?: number) => {
      const lastUpdatedTime = lastSyncTime || lastUpdatedTimestampRef.current;
      if (lastUpdatedTime) {
        const { elapsedHours } = getTimeDiffJson(lastUpdatedTime);

        if (elapsedHours > maxIdleHoursBeforeForcedReconnect) {
          window.location.reload();
          return;
        }
      }

      socket.disconnect();
      socket.connect();
      onReconnectRef.current?.(lastUpdatedTime);
      lastUpdatedTimestampRef.current = new Date().getTime();
      // lastSocketMsgTimeRef.current = lastUpdatedTimestampRef.current;
    };
    const reConnect = () => {
      if (isConnectedRef.current && socket.connected) {
        updateSocketStatus();
        onConnectionStatusChange?.(true);
        return;
      }
      refreshChat();
    };

    const handleOnVisibilityChange = () => {
      trackSocketEventActionType({
        type: SocketEventActionType.SOCKET_TAB_VISIBILITY_CHANGED,
        socketName,
        state: document.visibilityState,
      });
      if (document.visibilityState === 'visible') {
        if (reconnectOnVisibility) {
          reConnect();
        }
      }
    };
    const handleOnFocus = () => {
      if (reconnectOnFocus) {
        reConnect();
      }
    };

    window.addEventListener('focus', handleOnFocus);
    window.addEventListener('online', refreshChat);
    window.addEventListener('visibilitychange', handleOnVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleOnFocus);
      window.removeEventListener('online', refreshChat);
      window.removeEventListener('visibilitychange', handleOnVisibilityChange);
      // window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [socket, socket.connected, socketName]);

  return { socket, addListener, removeListener };
};

export default useSocket;
